import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import DataTable from "./../../../Layout/Table/TableLayout";
import Navbar from "../../../Shared/Navbar/Navbar";
import { FaEye, FaEdit, FaTrash, FaCopy } from "react-icons/fa";
import { baseURL } from "../../../Apiservices/Api";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../../AuthContext/AuthContext";
import Loader from "../../../Loader/Loader";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

const AdminCustomer = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedTag, setSelectedTag] = useState("");
  const [pageIndex, setPageIndex] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { authToken } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // ===============================
  // FETCH CUSTOMERS (MAIN QUERY)
  // ===============================
  const {
    data: customers = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin-customers", selectedTag, authToken],
    refetchInterval: 3000, // Refetch every 3 seconds to keep data updated
    queryFn: async () => {
      if (!authToken) return [];

      // If tag selected → use tag API
      if (selectedTag) {
        const response = await axios.get(
          `${baseURL}/api/tagfilter`,
          {
            params: { tag: selectedTag },
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        return response.data;
      }

      // Otherwise fetch all customers
      const response = await axios.get(
        `${baseURL}/api/customers`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      return response.data
        .filter((c) => c.customer_status === "existing")
        .map((c) => ({
          ...c,
          formattedId: `CUS${String(c.id).padStart(4, "0")}`,
        }));
    },
    enabled: !!authToken,
    keepPreviousData: true,
  });

  // ===============================
  // FETCH TAGS
  // ===============================
  const { data: tags = [] } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const response = await axios.get(`${baseURL}/api/tags`);
      return response.data;
    },
  });

  // ===============================
  // DELETE CUSTOMER MUTATION
  // ===============================
  const deleteMutation = useMutation({
    mutationFn: async (customerId) => {
      await axios.delete(`${baseURL}/api/customers/${customerId}`);
    },
    onSuccess: (_, customerId) => {
      queryClient.setQueryData(
        ["admin-customers", selectedTag, authToken],
        (oldData = []) =>
          oldData.filter((c) => c.id !== customerId)
      );

      setMessage("Customer deleted successfully");
      setTimeout(() => setMessage(null), 2000);
    },
    onError: () => {
      setMessage("Failed to delete customer");
      setTimeout(() => setMessage(null), 2000);
    },
  });

  // ===============================
  // PAGE FROM URL
  // ===============================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageFromUrl = parseInt(params.get("page"));

    if (!isNaN(pageFromUrl)) {
      setPageIndex(pageFromUrl - 1);
    }
  }, [location.search]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage("Copied to clipboard!");
      setTimeout(() => setMessage(null), 1000);
    });
  };

  const handleDeleteCustomer = (customerId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this Customer?"
    );
    if (!confirmDelete) return;

    deleteMutation.mutate(customerId);
  };

  const navigateToCustomerDetails = (customerId, currentPage) => {
            const params = new URLSearchParams(location.search);
  const pageSize = params.get('pageSize') || '20';

    navigate(`/a-customerdetails/${customerId}?page=${currentPage + 1}&pageSize=${pageSize}`);
  };

  const navigateToEditLead = (customerId, currentPage) => {
    const params = new URLSearchParams(location.search);
  const pageSize = params.get('pageSize') || '20';
    navigate(`/a-editcustomerdetails/${customerId}?page=${currentPage + 1}&pageSize=${pageSize}`);
  };

  // ===============================
  // TABLE COLUMNS
  // ===============================
  const columns = useMemo(
    () => [
      { Header: "Customer ID", accessor: "id" },
      {
        Header: "Name",
        accessor: "name",
             Cell: ({ row }) => {
          const currentPage = pageIndex; 
              const params = new URLSearchParams(location.search);
    const pageSize = params.get('pageSize') || '20';
          return (
          <div
            style={{
              cursor: "pointer",
              color: "blue",
              textDecoration: "underline",
            }}
            onClick={() =>
              navigateToCustomerDetails(
                row.original.id,
currentPage, pageSize
              )
            }
          >
            {row.original.name}
          </div>
        )
       }

      },
      {
        Header: "Mobile",
        accessor: "phone_number",
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <a
              href={`https://wa.me/${row.original.phone_number}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "blue" }}
            >
              {row.original.phone_number}
            </a>
            <FaCopy
              style={{
                marginLeft: "8px",
                cursor: "pointer",
                color: "#ff9966",
              }}
              onClick={() =>
                copyToClipboard(row.original.phone_number)
              }
            />
          </div>
        ),
      },
      { Header: "Email", accessor: "email" },
      { Header: "Origin City", accessor: "origincity" },
      {
        Header: "Actions",
        Cell: ({ row }) => {
                const currentPage = pageIndex; // Capture current page index
              const params = new URLSearchParams(location.search);
        const pageSize = params.get('pageSize') || '20';


        return (
          <div style={{ display: "flex", gap: "10px" }}>
            <FaEye
              style={{ cursor: "pointer", color: "#ff9966" }}
              onClick={() =>
                navigateToCustomerDetails(
                  row.original.id,
                  pageIndex, pageSize,currentPage
                )
              }
            />
            <FaTrash
              style={{ cursor: "pointer", color: "#ff9966" }}
              onClick={() =>
                handleDeleteCustomer(row.original.id)
              }
            />
            <FaEdit
              style={{ cursor: "pointer", color: "#ff9966" }}
              onClick={() =>
                navigateToEditLead(
                  row.original.id,
                  pageIndex,currentPage,pageSize
                )
              }
            />
          </div>
        )
        },
      },
    ],
    [pageIndex,location]
  );

  return (
    <div className="AdminCustomercontainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`AdminCustomer ${collapsed ? "collapsed" : ""}`}>
        <div className="AdminCustomer-container mb-5">
          <div className="AdminCustomer-table-container">

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3>Customer Details</h3>
              <select
                className="form-select w-25"
                value={selectedTag}
                onChange={(e) => {
                  setSelectedTag(e.target.value);
                  setPageIndex(0);
                }}
              >
                <option value="">All Tags</option>
                {tags.map((tag) => (
                  <option key={tag.id} value={tag.value}>
                    {tag.value}
                  </option>
                ))}
              </select>
            </div>

            {message && (
              <div className="alert alert-success">
                {message}
              </div>
            )}

            {isLoading ? (
              <Loader />
            ) : isError ? (
              <div className="text-danger p-4">
                {error?.message}
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center p-5">
                <p>No data available</p>
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={customers}
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomer;
